import { useEffect, useRef, useState } from "react";
import { Instagram, ExternalLink, Play, Copy } from "lucide-react";
import { CURATED_POSTS, INSTAGRAM_HANDLE, INSTAGRAM_URL } from "@/lib/instagram";
import { Reveal } from "./Reveal";

const INITIAL_MOBILE = 4;

const InstagramEmbed = () => {
  const ref = useRef(null);
  const [load, setLoad] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setLoad(true);
          obs.disconnect();
        }
      },
      { rootMargin: "400px" }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} data-testid="instagram-embed" className="mt-10 flex flex-col items-center">
      <div className="w-full max-w-2xl bg-white border border-[var(--brand-gold-soft)] shadow-sm">
        <div className="h-1 bg-[var(--brand-gold)]" aria-hidden="true" />
        {load ? (
          <iframe
            src="https://www.instagram.com/allinprecisionwellnesspa/embed"
            title="Instagram feed from All-In Precision Wellness"
            loading="lazy"
            className="w-full h-[480px] sm:h-[560px] border-0"
            allowTransparency="true"
            scrolling="yes"
          />
        ) : (
          <div className="h-[480px] sm:h-[560px] flex items-center justify-center text-[var(--brand-muted)] text-sm">
            Loading Instagram&hellip;
          </div>
        )}
      </div>
      <a
        data-testid="instagram-embed-fallback-link"
        href={INSTAGRAM_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-4 inline-flex items-center gap-2 text-sm text-[var(--brand-muted)] hover:text-[var(--brand-navy)] transition-colors"
      >
        Feed not loading? View {INSTAGRAM_HANDLE} on Instagram{" "}
        <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
      </a>
    </div>
  );
};

const PostCard = ({ post, testId }) => (
  <a
    data-testid={testId}
    href={post.url}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={`Open Instagram post: ${post.alt}`}
    className="group relative block aspect-square overflow-hidden bg-[var(--brand-navy-deep)]"
  >
    <img
      src={post.thumbnail}
      alt={post.alt}
      loading="lazy"
      className="h-full w-full object-cover group-hover:scale-[1.04] group-hover:opacity-90 transition-all duration-500"
    />
    {post.isVideo && (
      <span className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-black/50 group-hover:bg-[var(--brand-gold)] transition-colors">
          <Play className="h-5 w-5 text-white fill-white" />
        </span>
      </span>
    )}
    {post.isCarousel && (
      <span className="absolute top-2 right-2" aria-hidden="true">
        <Copy className="h-4 w-4 text-white drop-shadow" />
      </span>
    )}
  </a>
);

export const InstagramSection = ({ variant, onStartGuide }) => {
  const [showAll, setShowAll] = useState(false);
  const posts = [...CURATED_POSTS].sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0)).slice(0, 6);

  return (
    <section data-testid="instagram-section" className="py-16 sm:py-24 bg-[var(--brand-cream)] border-y border-[var(--brand-gold-soft)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
        <Reveal>
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.3em] text-[var(--brand-gold)] mb-4">
            Follow along
          </p>
          <h2 className="font-serif-brand text-3xl sm:text-4xl lg:text-5xl font-semibold text-[var(--brand-navy)] break-words">
            <span className="text-[var(--brand-gold)]">@allinprecisionwellnesspa</span> on Instagram
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-[var(--brand-muted)] max-w-2xl mx-auto">
            Get to know Dr. Jill before your first visit. Explore conversations about health, learn
            about her approach, and take a look inside All-In Precision Wellness.
          </p>
        </Reveal>

        {posts.length > 0 ? (
          <Reveal delay={0.1}>
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
              {posts.map((p, i) => (
                <div key={p.url} className={i >= INITIAL_MOBILE && !showAll ? "hidden sm:block" : ""}>
                  <PostCard post={p} testId={`instagram-post-${i}`} />
                </div>
              ))}
            </div>
            {posts.length > INITIAL_MOBILE && !showAll && (
              <button
                data-testid="instagram-show-more"
                onClick={() => setShowAll(true)}
                className="mt-6 sm:hidden w-full border-2 border-[var(--brand-navy)] text-[var(--brand-navy)] font-semibold py-3 bg-white"
              >
                Show more
              </button>
            )}
          </Reveal>
        ) : (
          <Reveal delay={0.1}>
            <InstagramEmbed />
          </Reveal>
        )}

        <Reveal delay={0.15}>
          <a
            data-testid="instagram-follow-link"
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center justify-center gap-2.5 bg-[var(--brand-navy)] text-white text-base sm:text-lg font-semibold px-8 py-3.5 hover:bg-[var(--brand-gold)] hover:text-[var(--brand-navy-deep)] transition-colors"
          >
            <Instagram className="h-5 w-5" aria-hidden="true" /> Follow on Instagram
          </a>
          <div className="mt-12 border-t border-[var(--brand-gold-soft)] pt-10">
            <h3 className="font-serif-brand text-2xl sm:text-3xl font-medium text-[var(--brand-navy)]">
              Ready to explore your next step?
            </h3>
            <button
              data-testid="instagram-cta-button"
              onClick={onStartGuide}
              className="mt-5 inline-flex items-center justify-center bg-[var(--brand-navy)] text-white text-lg font-semibold px-10 py-4 hover:bg-[var(--brand-navy-deep)] transition-colors"
            >
              Find My Next Step
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
